import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Trophy, Users, RotateCcw, Info } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

/**
 * /admin/knockouts — Editable KO Bracket UI.
 * Two drag sources:
 *   (1) "Qualified Pool" — top 8 teams by standings (drag onto any KO slot)
 *   (2) Inter-match swap  — drag a team from one KO card onto another KO card's slot
 *
 * Uses HTML5 native drag-and-drop. Persists via PATCH /api/matches/{id}/teams.
 * If the target match has a tie_id, all 3 seed matches update atomically.
 */
const STAGE_ORDER = ["quarter", "semi", "final"];
const STAGE_LABEL = { quarter: "Quarterfinals", semi: "Semifinals", final: "Grand Final" };

export default function KnockoutBracket() {
  const [teams, setTeams] = useState([]);
  const [standings, setStandings] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dragged, setDragged] = useState(null); // {teamId, sourceMatchId?, side?}

  const load = async () => {
    try {
      const [{ data: tData }, { data: sData }, { data: mData }] = await Promise.all([
        api.get("/teams"),
        api.get("/public/standings"),
        api.get("/matches"),
      ]);
      setTeams(tData || []);
      setStandings(sData || []);
      setMatches((mData || []).filter((m) => ["quarter", "semi", "final"].includes(m.stage)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load bracket");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const teamById = useMemo(() => Object.fromEntries((teams || []).map((t) => [t.id, t])), [teams]);

  // Group KO matches by stage & tie_id — one card per tie.
  const koCards = useMemo(() => {
    const seen = new Map();
    for (const m of matches) {
      const key = m.tie_id || m.id;
      if (!seen.has(key)) {
        seen.set(key, {
          key,
          tie_id: m.tie_id,
          match_id: m.id,
          stage: m.stage,
          team_a_id: m.team_a_id,
          team_b_id: m.team_b_id,
          scheduled_at: m.scheduled_at,
          court: m.court,
          status: m.status,
        });
      }
    }
    return Array.from(seen.values()).sort((a, b) =>
      STAGE_ORDER.indexOf(a.stage) - STAGE_ORDER.indexOf(b.stage) ||
      (a.scheduled_at || "").localeCompare(b.scheduled_at || "")
    );
  }, [matches]);

  const byStage = useMemo(() => {
    const g = { quarter: [], semi: [], final: [] };
    for (const c of koCards) g[c.stage].push(c);
    return g;
  }, [koCards]);

  const pool = useMemo(() => {
    // Top 8 by standings (fallback to first 8 teams)
    const ranked = (standings || []).slice(0, 8).map((s) => s.team_id || s.id).filter(Boolean);
    if (ranked.length >= 4) return ranked.map((id) => teamById[id]).filter(Boolean);
    return (teams || []).slice(0, 8);
  }, [standings, teams, teamById]);

  const onDragStart = (payload) => (e) => {
    setDragged(payload);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", JSON.stringify(payload));
  };

  const onDropSlot = (card, side) => async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const payload = dragged || (() => { try { return JSON.parse(e.dataTransfer.getData("text/plain")); } catch { return null; } })();
    setDragged(null);
    if (!payload || !payload.teamId) return;

    if (card.status === "live" || card.status === "finished") {
      toast.error(`Cannot edit a ${card.status} match`);
      return;
    }
    // If dropping onto the same team, no-op.
    const currentId = side === "a" ? card.team_a_id : card.team_b_id;
    if (currentId === payload.teamId) return;

    setBusy(true);
    try {
      // If this was an inter-match swap, we do BOTH updates atomically:
      //   1) target card side ← dragged team
      //   2) source card side ← the team we just displaced from target (swap)
      if (payload.sourceMatchId && payload.sourceMatchId !== card.match_id) {
        const displaced = currentId;
        const targetBody = side === "a" ? { team_a_id: payload.teamId } : { team_b_id: payload.teamId };
        const sourceBody = payload.side === "a" ? { team_a_id: displaced } : { team_b_id: displaced };
        await api.patch(`/matches/${card.match_id}/teams`, targetBody);
        if (displaced) await api.patch(`/matches/${payload.sourceMatchId}/teams`, sourceBody);
        toast.success("Teams swapped");
      } else {
        const body = side === "a" ? { team_a_id: payload.teamId } : { team_b_id: payload.teamId };
        await api.patch(`/matches/${card.match_id}/teams`, body);
        toast.success("Team reassigned");
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reassignment failed");
    } finally {
      setBusy(false);
    }
  };

  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };

  if (loading) return <div className="p-8 text-zinc-400">Loading bracket…</div>;

  return (
    <div className="p-6 md:p-8 min-h-screen bg-[#060d09] text-white" data-testid="knockout-bracket-page">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <Trophy className="w-5 h-5 text-[#F4B400]" />
          <h1 className="font-anton text-4xl uppercase tracking-tight text-white">Editable Knockout Bracket</h1>
        </div>
        <p className="text-sm text-zinc-400">
          Drag a team from the <b>Qualified Pool</b> onto any KO slot to assign it, or drag a team from one KO card onto another to swap. Changes persist immediately and are audit-logged. Live and finished matches are locked.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Left column: Qualified Pool */}
        <Card className="p-4 bg-[#0f1a15] border-white/10" data-testid="ko-pool">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-[#00A86B]" />
            <h2 className="font-anton text-lg uppercase tracking-tight text-white">Qualified Pool</h2>
          </div>
          <p className="text-[10px] text-zinc-500 mb-3">Top 8 by standings — drag onto any KO slot</p>
          <div className="space-y-2">
            {pool.map((t, i) => (
              <div
                key={t.id}
                draggable
                onDragStart={onDragStart({ teamId: t.id })}
                className="flex items-center gap-2 p-2 rounded-lg border border-white/10 bg-black/30 cursor-grab active:cursor-grabbing hover:border-[#F4B400]/40"
                data-testid={`pool-team-${t.id}`}
              >
                <div className="w-6 text-center text-[10px] text-zinc-500">#{i + 1}</div>
                <div className="w-6 h-6 rounded bg-black/40 overflow-hidden">
                  {t.logo_url && <img src={t.logo_url} alt="" className="w-full h-full object-contain" />}
                </div>
                <div className="text-xs text-white truncate flex-1">{t.name}</div>
                {t.group && <span className="text-[9px] text-zinc-500">Grp {t.group}</span>}
              </div>
            ))}
            {pool.length === 0 && <div className="text-xs text-zinc-500 italic py-4 text-center">No teams available</div>}
          </div>
        </Card>

        {/* Right: 3 stage columns */}
        <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-4">
          {STAGE_ORDER.map((stage) => (
            <div key={stage} data-testid={`ko-column-${stage}`}>
              <div className="font-anton text-lg uppercase tracking-tight text-[#F4B400] mb-2">{STAGE_LABEL[stage]}</div>
              <div className="space-y-3">
                {byStage[stage].length === 0 && (
                  <Card className="p-4 border-dashed border-white/10 bg-black/20 text-center text-xs text-zinc-500">
                    No {STAGE_LABEL[stage]} matches yet — create via the Draw Generator.
                  </Card>
                )}
                {byStage[stage].map((card) => (
                  <KoCard
                    key={card.key}
                    card={card}
                    teamById={teamById}
                    onDragStart={onDragStart}
                    onDropSlot={onDropSlot}
                    onDragOver={onDragOver}
                    disabled={busy}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 text-[11px] text-zinc-500 flex items-start gap-2 max-w-3xl">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#F4B400]" />
        Backend enforces: only matches with <code>status ∈ {"{"} scheduled {"}"}</code> can be edited. If a match belongs to a tie (3 seed matches), all 3 are updated together.
      </div>
    </div>
  );
}

function KoCard({ card, teamById, onDragStart, onDropSlot, onDragOver, disabled }) {
  const a = teamById[card.team_a_id];
  const b = teamById[card.team_b_id];
  const locked = card.status === "live" || card.status === "finished";
  return (
    <Card className={`p-3 bg-[#0f1a15] border-white/10 ${locked ? "opacity-70" : ""}`} data-testid={`ko-card-${card.match_id}`}>
      <div className="flex items-center justify-between mb-2 text-[10px] uppercase tracking-widest">
        <span className="text-zinc-500">{card.court || "TBD"}</span>
        <span className={`px-1.5 py-0.5 rounded ${locked ? "bg-red-500/20 text-red-300" : "bg-emerald-500/20 text-emerald-300"}`}>
          {card.status || "scheduled"}
        </span>
      </div>
      <Slot side="a" team={a} card={card} onDragStart={onDragStart} onDropSlot={onDropSlot} onDragOver={onDragOver} disabled={disabled || locked} />
      <div className="text-center text-[9px] uppercase tracking-widest text-zinc-500 my-1">vs</div>
      <Slot side="b" team={b} card={card} onDragStart={onDragStart} onDropSlot={onDropSlot} onDragOver={onDragOver} disabled={disabled || locked} />
      <div className="mt-2 flex items-center justify-between text-[9px] text-zinc-500">
        <span>{card.scheduled_at ? new Date(card.scheduled_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "TBD"}</span>
        <Link to={`/matches/${card.match_id}`} className="text-[#F4B400] hover:underline">View →</Link>
      </div>
    </Card>
  );
}

function Slot({ side, team, card, onDragStart, onDropSlot, onDragOver, disabled }) {
  return (
    <div
      onDragOver={disabled ? undefined : onDragOver}
      onDrop={disabled ? undefined : onDropSlot(card, side)}
      className={`flex items-center gap-2 p-2 rounded-lg border ${disabled ? "border-white/5 bg-black/20" : "border-white/10 bg-black/40 hover:border-[#F4B400]/40"} min-h-[44px]`}
      data-testid={`ko-slot-${card.match_id}-${side}`}
    >
      {team ? (
        <div
          draggable={!disabled}
          onDragStart={disabled ? undefined : onDragStart({ teamId: team.id, sourceMatchId: card.match_id, side })}
          className="flex items-center gap-2 w-full cursor-grab active:cursor-grabbing"
        >
          <div className="w-5 h-5 rounded bg-black/40 overflow-hidden">
            {team.logo_url && <img src={team.logo_url} alt="" className="w-full h-full object-contain" />}
          </div>
          <div className="text-xs text-white truncate flex-1">{team.name}</div>
        </div>
      ) : (
        <div className="text-[10px] uppercase text-zinc-500 italic w-full text-center">Drop team here</div>
      )}
    </div>
  );
}
